import { Helmet } from 'react-helmet-async';
import { useState, useEffect, useMemo, useContext, useRef } from 'react';
// @mui
import {
  Card,
  Container,
  Stack,
  Button,
  Box,
  Autocomplete,
  TextField
} from '@mui/material';
import MUIDataTable from "mui-datatables";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { id } from "date-fns/locale";
import { endOfDay } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { TableLoader } from '../../components/table';
import { getCurrentDate } from "../../utils/formatDate";
// Context
import { axiosPrivate } from '../../common/axiosPrivate';
import AuthContext from '../../context/authContext';

export default function PenagihanEksternalPage() {
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);
  const { user } = useContext(AuthContext);
  const [data, setData] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [petugas, setPetugas] = useState("");
  const [petugasList, setPetugasList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const BodyComponent = useMemo(
    () => (props) => (
      <TableLoader loading={tableLoading} {...props} />
    ),
    [tableLoading]
  );

  const columns = [
    {
      name: "number",
      label: "No",
      options: {
        filter: false,
        sort: false,
        customBodyRender : (value, tableMeta) => {
          const rowIndex = Number(tableMeta.rowIndex) + 1;
          return rowIndex;
        }
      },
    },
    {
      name: "kantor",
      label: "Kantor",
      options: {
       filter: true,
       sort: true,
       customBodyRender: (value) => value?.nama
      }
    },
    {
      name: "rumpun",
      label: "Rumpun",
      options: {
       filter: true,
       sort: true,
       customBodyRender: (value) => value?.nama
      }
    },
    {
      name: "anggota",
      label: "Anggota",
      options: {
       filter: true,
       sort: true,
       customBodyRender: (value) => value?.nama
      }
    },
    {
      name: "petugas",
      label: "Petugas",
      options: {
       filter: true,
       sort: true,
       customBodyRender: (value) => value?.nama
      }
    },
    {
      name: "tanggal",
      label: "Tanggal",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => formatInTimeZone(new Date(value), 'UTC', 'dd/MM/yyyy HH:mm')
      }
    },
    {
      name: "nominal",
      label: "Nominal",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          const number = Number(value);
          if (!value || Number.isNaN(number) || number === 0) return "-";
          return number.toLocaleString("id-ID"); // hasil: 1.000.000
        }
      }
    },   
  ];
   
  const options = {
    filterType: "dropdown",
    selectableRowsHideCheckboxes: true,
    downloadOptions: {
      filename: `Absen-${user?.kantorName}-${getCurrentDate('-')}.csv`,
      filterOptions: {
        useDisplayedColumnsOnly: false,
        useDisplayedRowsOnly: true,
      }
    },
    onTableChange: (action, tableState) => {
    if (['sort', 'filterChange', 'resetFilters', 'search', 'changePage', 'changeRowsPerPage'].includes(action)) {
      // `tableState.displayData` adalah array dengan index baris yang ditampilkan
      const shownIndexes = tableState.displayData.map(d => d.dataIndex);
      setFilteredData(shownIndexes.map(idx => data[idx]));
    }
  },
  // ... customFooter di bawah
  customFooter: () => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        fontWeight: 700,
        fontSize: 16,
        p: 2,
        pr: 6,
        borderTop: '1px solid #eee',
        background: '#f8f8f8'
      }}
    >
      Total Penerimaan:&nbsp;
      <span style={{ minWidth: 120, textAlign: 'right' }}>
        {(filteredData.length ? filteredData : data)
          .reduce((sum, row) => sum + (Number(row.nominal) || 0), 0)
          .toLocaleString('id-ID')}
      </span>
    </Box>
  ),
  };
  
  const handleStartDateChange = (date) => {
    startDateRef.current = date;
  };

  const handleEndDateChange = (date) => {
    endDateRef.current = date;
  };

  const filterData = () => {
    setTableLoading(true);

    const modEndDate = endOfDay(endDateRef.current);

    // Compose payload sesuai role
    const payload = {
      startDate: startDateRef.current,
      endDate: modEndDate,
      petugas
    };

    axiosPrivate.post('/laporan/monitoring-penagihan-mis', payload)
      .then((response) => {
        setData(response?.data);
      })
      .catch((error) => console.log(error))
      .finally(() => setTableLoading(false));
  };

  useEffect(() => {
    const fetchPetugasList = async () => {
      try {
        const response = await axiosPrivate.get("/penagihan/dropdown-eksternal", {
          params: {
            kantorList: user.kantorList.join(',')
          }
        });

        setPetugasList(response.data || []);
      } catch (error) {
        console.error("Error fetching petugas list:", error);
      }
    };
    fetchPetugasList();
  }, [user?.kantor, user?.role, user?.kantorList]);

  return (
    <>
      <Helmet>
        <title> Monitoring Penagihan | Sip App </title>
      </Helmet>
      <Container
        sx={{
          mt: 5
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mb: 2
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="start">
            <Box sx={{ marginRight: 2 }}>
              <LocalizationProvider adapterLocale={id} dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Dari Tanggal"
                  value={startDateRef.current}
                  onChange={handleStartDateChange}
                  slotProps={{ textField: { size: 'small' } }}
                />
              </LocalizationProvider>
            </Box>

            <Box sx={{ marginRight: 2 }}>
              <LocalizationProvider adapterLocale={id} dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Sampai Tanggal"
                  value={endDateRef.current}
                  onChange={handleEndDateChange}
                  slotProps={{ textField: { size: 'small' } }}
                />
              </LocalizationProvider>
            </Box>

            <Box sx={{ marginRight: 2 }}>
              <Autocomplete
                options={petugasList}
                getOptionLabel={(option) => option.nama}
                value={petugasList.find((p) => p.id === petugas) || null}
                onChange={(_, newValue) => setPetugas(newValue ? newValue.id : "")}
                sx={{ width: 240, height: 40 }}
                renderInput={(params) => (
                  <TextField {...params} label="Petugas" size="small" />
                )}
              />
            </Box>

            <Button variant="contained" onClick={filterData}>
              Filter
            </Button>
          </Stack>
        </Box>
        <Card>
          <MUIDataTable
            title="Monitoring Penagihan"
            data={data}
            columns={columns}
            options={options}
            components={{ TableBody: BodyComponent }}
          />
        </Card>
      </Container>
    </>
  );
}
